import type { AbilityScores } from "../types/entity.js";
import { randomInt } from "../combat/dice.js";

/**
 * 5e-style "full" point-buy rules for the character creator (C03).
 *
 * Ability scores are chosen before racial bonuses are applied. Each score
 * starts at `BASE_SCORE` and can be raised up to `MAX_SCORE` using the
 * `COST_TABLE` budget; lowering below `BASE_SCORE` is not permitted.
 *
 * v1 follows the SRD variant-rule curve up to 15, then extends the table
 * to 18 with progressively steeper costs (16:12, 17:15, 18:19). Pool stays
 * at the SRD 27 — to reach an 18 you have to dump several other stats,
 * which keeps the budget honest while letting the dice random-spend land
 * on classic-D&D high rolls.
 */

export const POINT_POOL = 27;
export const BASE_SCORE = 8;
export const MIN_SCORE = 8;
export const MAX_SCORE = 18;

/**
 * Cumulative cost to raise a single ability to the target score from
 * `BASE_SCORE`. The jump at 14 and 15 reflects the SRD variant rule;
 * 16–18 extend that curve so an 18 is achievable but expensive.
 * Keys outside [MIN_SCORE, MAX_SCORE] have no entry.
 */
export const COST_TABLE: Readonly<Record<number, number>> = {
  8: 0,
  9: 1,
  10: 2,
  11: 3,
  12: 4,
  13: 5,
  14: 7,
  15: 9,
  16: 12,
  17: 15,
  18: 19,
};

/** Returns the point cost of a single ability score, or null if out of range. */
export function scoreCost(score: number): number | null {
  const cost = COST_TABLE[score];
  return cost === undefined ? null : cost;
}

/**
 * Returns the total point cost of an ability-score array, or `null` if
 * any individual score is out of the allowed range.
 */
export function totalCost(scores: AbilityScores): number | null {
  let total = 0;
  for (const ability of Object.keys(scores) as (keyof AbilityScores)[]) {
    const c = scoreCost(scores[ability]);
    if (c === null) return null;
    total += c;
  }
  return total;
}

/** Default "all 8" starting allocation with zero points spent. */
export function createDefaultAllocation(): AbilityScores {
  return { STR: BASE_SCORE, DEX: BASE_SCORE, CON: BASE_SCORE, INT: BASE_SCORE, WIS: BASE_SCORE, CHA: BASE_SCORE };
}

/** Points left after spending — negative means over-budget. */
export function remainingPoints(scores: AbilityScores, pool: number = POINT_POOL): number {
  const total = totalCost(scores);
  return total === null ? Number.NaN : pool - total;
}

export type AllocationIssue =
  | { readonly kind: "out_of_range"; readonly ability: keyof AbilityScores; readonly score: number }
  | { readonly kind: "over_budget"; readonly spent: number; readonly pool: number };

export interface ValidationResult {
  readonly valid: boolean;
  readonly issues: readonly AllocationIssue[];
}

/**
 * Checks that every score is inside the allowed range and the total cost
 * fits the pool. Collects all issues so the UI can display them at once
 * rather than stopping at the first failure.
 */
export function validateAllocation(
  scores: AbilityScores,
  pool: number = POINT_POOL,
): ValidationResult {
  const issues: AllocationIssue[] = [];
  let spent = 0;
  for (const ability of Object.keys(scores) as (keyof AbilityScores)[]) {
    const score = scores[ability];
    const cost = scoreCost(score);
    if (cost === null) {
      issues.push({ kind: "out_of_range", ability, score });
    } else {
      spent += cost;
    }
  }
  if (spent > pool) {
    issues.push({ kind: "over_budget", spent, pool });
  }
  return { valid: issues.length === 0, issues };
}

/**
 * Returns a new allocation with `ability` incremented by 1, or the original
 * object if the bump would exceed MAX_SCORE or the remaining point budget.
 * Never mutates its input.
 */
export function increment(
  scores: AbilityScores,
  ability: keyof AbilityScores,
  pool: number = POINT_POOL,
): AbilityScores {
  const next = scores[ability] + 1;
  if (next > MAX_SCORE) return scores;
  const nextCost = scoreCost(next);
  const curCost = scoreCost(scores[ability]);
  if (nextCost === null || curCost === null) return scores;
  const delta = nextCost - curCost;
  if (remainingPoints(scores, pool) < delta) return scores;
  return { ...scores, [ability]: next };
}

/**
 * Returns a new allocation with `ability` decremented by 1, or the
 * original object if the score is already at MIN_SCORE. Never mutates
 * its input.
 */
export function decrement(scores: AbilityScores, ability: keyof AbilityScores): AbilityScores {
  const next = scores[ability] - 1;
  if (next < MIN_SCORE) return scores;
  return { ...scores, [ability]: next };
}

/**
 * Spends the full pool randomly across the six abilities, producing a
 * valid allocation. Starts from `createDefaultAllocation()` and repeatedly
 * picks a random ability that can still be incremented until no further
 * increment is affordable.
 *
 * Uses `randomInt` from the dice module so tests and saves can seed the
 * RNG (CLAUDE.md: no direct `Math.random` in `src/engine/`). Always
 * terminates — each iteration strictly lowers the remaining point budget
 * or shrinks the candidate set.
 */
export function randomAllocation(pool: number = POINT_POOL): AbilityScores {
  const abilities: (keyof AbilityScores)[] = ["STR", "DEX", "CON", "INT", "WIS", "CHA"];
  let scores = createDefaultAllocation();

  // Guard against pathological pools that can't buy anything.
  if (pool <= 0) return scores;

  // Upper bound: we can only make a finite number of increments before
  // every ability hits MAX_SCORE or we run out of points.
  const maxIters = abilities.length * (MAX_SCORE - BASE_SCORE);
  for (let i = 0; i < maxIters; i++) {
    const candidates = abilities.filter((a) => increment(scores, a, pool) !== scores);
    if (candidates.length === 0) break;
    const pick = candidates[randomInt(0, candidates.length - 1)]!;
    scores = increment(scores, pick, pool);
  }
  return scores;
}

/**
 * Applies racial bonuses on top of a point-bought allocation. Pure —
 * returns a new AbilityScores object. Caller is responsible for mapping
 * SRDRace.ability_bonuses (short names) into the Partial<AbilityScores>
 * used here.
 */
export function applyRacialBonuses(
  scores: AbilityScores,
  bonuses: Partial<Record<keyof AbilityScores, number>>,
): AbilityScores {
  const out: AbilityScores = { ...scores };
  for (const key of Object.keys(bonuses) as (keyof AbilityScores)[]) {
    const delta = bonuses[key] ?? 0;
    out[key] = out[key] + delta;
  }
  return out;
}
